import * as React from 'react';
import { IconProps } from './types';

type DecoratorPosition = 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';

type IconWithDecoratorOptions = {
  position?: DecoratorPosition;
  scale?: number;
  /** Draws a background circle behind the decorator so it stays legible over the base icon. Defaults to true. */
  knockout?: boolean;
  knockoutColor?: string;
};

const VIEWBOX_SIZE = 28;
const ANCHORS: Record<DecoratorPosition, { x: number; y: number }> = {
  'top-left': { x: 0, y: 0 },
  'top-right': { x: 1, y: 0 },
  'bottom-left': { x: 0, y: 1 },
  'bottom-right': { x: 1, y: 1 },
};

export const withDecorator = (
  BaseIcon: React.ComponentType<IconProps>,
  DecoratorIcon: React.ComponentType<IconProps>,
  options: IconWithDecoratorOptions = {}
) => {
  const {
    position = 'bottom-right',
    scale = 0.5,
    knockout = true,
    knockoutColor = 'rgba(4, 28, 74, var(--tw-bg-opacity))',
  } = options;
  const anchor = ANCHORS[position];
  const decoratorSize = VIEWBOX_SIZE * scale;
  const translateX = anchor.x * (VIEWBOX_SIZE - decoratorSize);
  const translateY = anchor.y * (VIEWBOX_SIZE - decoratorSize);

  const IconWithDecorator = (props: IconProps) => (
    <svg
      width="28px"
      height="28px"
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <BaseIcon />
      <g
        className="ohif-icon-decorator"
        transform={`translate(${translateX} ${translateY}) scale(${scale})`}
      >
        {knockout && (
          <circle
            cx={VIEWBOX_SIZE / 2}
            cy={VIEWBOX_SIZE / 2}
            r={VIEWBOX_SIZE / 2 + 3}
            fill={knockoutColor}
          />
        )}
        <DecoratorIcon />
      </g>
    </svg>
  );

  const baseName = BaseIcon.displayName || BaseIcon.name || 'Base';
  const decoratorName = DecoratorIcon.displayName || DecoratorIcon.name || 'Decorator';
  IconWithDecorator.displayName = `IconWithDecorator(${baseName}, ${decoratorName})`;

  return IconWithDecorator;
};
